import { useEffect, useMemo, useState } from 'react'
import { syncPending, refreshPendingCount } from '../api/sync'
import { api } from '../api/client'
import { MockSensorTransport } from '../ble/mockTransport'
import { channelSide } from '../ble/protocol'
import type { SensorInfo, SensorTransport } from '../ble/transport'
import { WebBluetoothTransport, webBluetoothSupported } from '../ble/webBluetoothTransport'
import { getHubFileBlob, recentHubSession, saveReadSession, type HubFileMeta, type HubSessionRef } from '../db/offlineDb'
import { appStore, useAppState } from '../state/appStore'
import type { Assignment, Pallet, Sensor } from '../types/domain'
import { sensorStatusLabel } from './format'
import { PalletSearchSelect } from './PalletSearchSelect'
import { WifiHubTransport } from '../wifi/hubTransport'

interface Props {
  pallets: Pallet[]
  sensors: Sensor[]
  onDataChanged: () => Promise<void>
}

export function ReadingWorkflow({ pallets, sensors, onDataChanged }: Props) {
  const app = useAppState()
  const candidates = useMemo(() => pallets
    .filter(pallet => pallet.status === 'READ_REQUIRED' || pallet.status === 'SENSORS_INSTALLED')
    .sort((left, right) => Number(right.status === 'READ_REQUIRED') - Number(left.status === 'READ_REQUIRED')), [pallets])
  const [palletId, setPalletId] = useState(candidates[0]?.id ?? pallets[0]?.id ?? '')
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [sensorId, setSensorId] = useState('')
  const [hubUrl, setHubUrl] = useState(() => {
    try { return window.localStorage.getItem('novaroll-hub-url') || import.meta.env.VITE_HUB_BASE_URL || '' }
    catch { return import.meta.env.VITE_HUB_BASE_URL || '' }
  })
  const [progress, setProgress] = useState<{ done: number; total?: number; unit: 'записей' | 'байт'; fileName?: string }>()
  const [phase, setPhase] = useState<'idle' | 'connecting' | 'reading' | 'saving' | 'syncing'>('idle')
  const [lastRead, setLastRead] = useState<
    | { kind: 'sensor'; info: SensorInfo; sideA: number; sideB: number; total: number }
    | { kind: 'hub'; info: SensorInfo; ref: HubSessionRef; files: HubFileMeta[] }
  >()

  useEffect(() => {
    if (!palletId) return
    setLastRead(undefined)
    setProgress(undefined)
    let active = true
    api.assignments(palletId).then(values => {
      if (!active) return
      setAssignments(values)
      setSensorId(current => values.some(value => value.sensorId === current) ? current : values[0]?.sensorId ?? '')
    }).catch(error => appStore.notify('error', error instanceof Error ? error.message : 'Не удалось загрузить назначения'))
    return () => { active = false }
  }, [palletId])

  useEffect(() => {
    setLastRead(undefined)
    setProgress(undefined)
  }, [sensorId, app.transportMode])

  useEffect(() => {
    if (!palletId || (app.transportMode !== 'web-bluetooth' && app.transportMode !== 'wifi-hub')) return
    let active = true
    recentHubSession(palletId).then(value => {
      if (active && value) setLastRead({
        kind: 'hub',
        info: { deviceId: 'local', name: `NOVAROLL-HUB-${value.ref.serialNumber}`, serialNumber: value.ref.serialNumber, protocolVersion: 2 },
        ...value,
      })
    }).catch(() => undefined)
    return () => { active = false }
  }, [palletId, app.transportMode])

  const selectedSensor = sensors.find(sensor => sensor.id === sensorId)
  const busy = phase !== 'idle'
  const transportSupported = app.transportMode !== 'web-bluetooth' || webBluetoothSupported()

  async function readWifiHub() {
    try {
      setPhase('connecting')
      setProgress(undefined)
      const transport = new WifiHubTransport(hubUrl)
      const manifest = await transport.connect()
      setPhase('reading')
      const { ref, files } = await transport.readFiles(palletId, (done, total, fileName) => {
        setProgress({ done, total, unit: 'байт', fileName })
      })
      setPhase('saving')
      setLastRead({ kind: 'hub', info: {
        deviceId: manifest.info.serialNumber, name: `NOVAROLL-HUB-${manifest.info.serialNumber}`,
        serialNumber: manifest.info.serialNumber, firmware: manifest.info.firmwareVersion,
        batteryPercent: manifest.info.batteryPercent, protocolVersion: 2, hub: manifest.info,
      }, ref, files })
      try {
        await transport.acknowledge()
        appStore.notify('success', `Сеанс ${ref.sessionId}: пять файлов сохранены и проверены; ACK подтверждён хабом`)
      } catch (error) {
        appStore.notify('error', `Файлы сохранены локально, но ACK не подтверждён: ${error instanceof Error ? error.message : 'ошибка хаба'}. Повторите считывание.`)
      }
    } catch (error) {
      appStore.notify('error', error instanceof Error ? error.message : 'Ошибка Wi-Fi-считывания')
      const saved = await recentHubSession(palletId).catch(() => undefined)
      if (saved) setLastRead({
        kind: 'hub', info: { deviceId: 'local', name: `NOVAROLL-HUB-${saved.ref.serialNumber}`,
          serialNumber: saved.ref.serialNumber, protocolVersion: 2 }, ...saved,
      })
    } finally {
      setPhase('idle')
    }
  }

  async function readSensor() {
    if (!palletId || busy || (app.transportMode === 'mock' && !selectedSensor)) return
    if (app.transportMode === 'wifi-hub') return readWifiHub()
    const transport: SensorTransport = app.transportMode === 'mock' ? new MockSensorTransport() : new WebBluetoothTransport()
    try {
      setPhase('connecting')
      setProgress(undefined)
      await transport.requestDevice(selectedSensor?.serialNumber)
      await transport.connect()
      const info = await transport.readInfo()
      if (info.hub && transport instanceof WebBluetoothTransport) {
        setPhase('reading')
        const { ref, files } = await transport.readHubFiles(info.hub, palletId, (done, total, fileName) => {
          setProgress({ done, total, unit: 'байт', fileName })
        })
        setPhase('saving')
        await transport.acknowledge()
        setLastRead({ kind: 'hub', info, ref, files })
        appStore.notify('success', `Сеанс ${info.hub.sessionId}: пять файлов проверены и сохранены локально; хаб подтвердил получение`)
        return
      }
      if (!selectedSensor) throw new Error('Для датчика BLE v1 сначала выберите установленный датчик')
      if (info.serialNumber && normalizeSerial(info.serialNumber) !== normalizeSerial(selectedSensor.serialNumber)) {
        throw new Error(`Выбран датчик ${info.serialNumber}, а на позиции зарегистрирован ${selectedSensor.serialNumber}`)
      }
      setPhase('reading')
      const samples = await transport.readSamples((done, total) => setProgress({ done, total, unit: 'записей' }))
      const sideA = samples.filter(sample => channelSide(sample.channel) === 'A').length
      const sideB = samples.filter(sample => channelSide(sample.channel) === 'B').length
      if (sideA === 0 || sideB === 0) throw new Error('Передача неполная: датчик должен вернуть измерения граней A и B')
      setPhase('saving')
      const startedAt = samples[0]?.measuredAt ?? new Date().toISOString()
      await saveReadSession(palletId, selectedSensor.id, startedAt, samples)
      await transport.acknowledge()
      await refreshPendingCount()
      setLastRead({ kind: 'sensor', info, sideA, sideB, total: samples.length })
      appStore.notify('success', `${info.name}: локально сохранено ${samples.length} измерений граней A и B`)
      if (navigator.onLine) {
        setPhase('syncing')
        const summary = await syncPending()
        if (summary.synced > 0) {
          appStore.notify('success', `На сервер отправлено ${summary.synced} пакет(а)${summary.conflicts ? ` · старых конфликтов в очереди: ${summary.conflicts}` : ''}`)
        } else if (summary.conflicts || summary.firstError) {
          appStore.notify('error', `Данные сохранены локально, но сервер отклонил пакет: ${summary.firstError ?? 'конфликт'}`)
        }
        await onDataChanged()
      }
    } catch (error) {
      appStore.notify('error', error instanceof Error ? error.message : 'Ошибка считывания')
      if (app.transportMode === 'web-bluetooth') {
        const saved = await recentHubSession(palletId).catch(() => undefined)
        if (saved) setLastRead({
          kind: 'hub',
          info: { deviceId: 'local', name: `NOVAROLL-HUB-${saved.ref.serialNumber}`, serialNumber: saved.ref.serialNumber, protocolVersion: 2 },
          ...saved,
        })
      }
    } finally {
      transport.disconnect()
      setPhase('idle')
    }
  }

  return (
    <section className="page-section">
      <div className="section-heading"><div><p className="eyebrow">Операторский сценарий</p><h1>{app.transportMode === 'wifi-hub' ? 'Считать хаб' : 'Считать датчик'}</h1></div><p className="section-note">По Wi-Fi PWA сохраняет пять файлов хаба локально с проверкой CRC. Для датчика v1 сохранён BLE-сценарий.</p></div>
      <div className="reading-grid">
        <article className="card reading-form">
          <div className="step"><span>1</span><div><strong>Выберите паллету</strong><small>В приоритете паллеты со статусом «требует считывания».</small></div></div>
          <PalletSearchSelect pallets={[...candidates, ...pallets.filter(pallet => !candidates.some(value => value.id === pallet.id))]}
            value={palletId} onChange={setPalletId} disabled={busy} showStatus={false} />
          {app.transportMode !== 'wifi-hub' && <><div className="step"><span>2</span><div><strong>Выберите установленный датчик</strong><small>Нужен для датчика BLE v1; хаб v2 отдаёт весь 16-канальный сеанс.</small></div></div>
          <label className="select-field wide ble-sensor-select"><span>Датчик и угловая позиция</span><select value={sensorId} onChange={event => setSensorId(event.target.value)} disabled={busy}>
            {assignments.map(assignment => {
              const sensor = sensors.find(value => value.id === assignment.sensorId)
              return <option key={assignment.id} value={assignment.sensorId}>{assignment.sensorSerial} · {cornerLabel(assignment.face)} · {assignment.level === 'TOP' ? 'верх' : 'низ'} · {sensor ? sensorStatusLabel[sensor.status] : ''}</option>
            })}
          </select></label></>}
          <div className="step"><span>{app.transportMode === 'wifi-hub' ? 2 : 3}</span><div><strong>Выберите транспорт</strong><small>Основной способ считывания хаба — Wi-Fi; BLE и Mock сохранены для совместимости.</small></div></div>
          <div className="transport-options">
            <label><input type="radio" name="transport" checked={app.transportMode === 'wifi-hub'} onChange={() => appStore.patch({ transportMode: 'wifi-hub' })} disabled={busy} /><span><strong>Хаб по Wi-Fi</strong><small>Пять файлов · возобновление</small></span></label>
            <label><input type="radio" name="transport" checked={app.transportMode === 'mock'} onChange={() => appStore.patch({ transportMode: 'mock' })} disabled={busy} /><span><strong>Mock BLE</strong><small>Для разработки и приёмки</small></span></label>
            <label className={!webBluetoothSupported() ? 'disabled' : ''}><input type="radio" name="transport" checked={app.transportMode === 'web-bluetooth'} onChange={() => appStore.patch({ transportMode: 'web-bluetooth' })} disabled={busy || !webBluetoothSupported()} /><span><strong>Датчик по BLE</strong><small>{webBluetoothSupported() ? 'Chrome / Edge · HTTPS' : 'Недоступен в этом браузере'}</small></span></label>
          </div>
          {app.transportMode === 'wifi-hub' && <div className="wifi-hub-settings">
            <label className="select-field wide"><span>Адрес хаба или HTTPS-шлюза</span><input type="url" value={hubUrl} onChange={event => {
              setHubUrl(event.target.value)
              try { window.localStorage.setItem('novaroll-hub-url', event.target.value) } catch { /* недоступное хранилище настроек */ }
            }} disabled={busy} placeholder="https://hub.example.local или /hub" autoComplete="url" /></label>
            <div className="ble-readiness" role="status"><strong>Подключите устройство к сети хаба</strong><small>Хаб должен быть доступен по доверенному HTTPS-адресу. Вариант /hub работает через настроенный шлюз того же домена. Прямой HTTP-адрес локального хаба из HTTPS-PWA браузер блокирует.</small></div>
          </div>}
          {app.transportMode === 'web-bluetooth' && <div className="ble-readiness" role="status">
            <strong>{webBluetoothSupported() ? 'Готово к поиску датчика' : 'Web Bluetooth не поддерживается'}</strong>
            <small>{webBluetoothSupported()
              ? 'Нажмите кнопку ниже для выбора устройства. Во время выгрузки файлов хаба оставляйте экран приложения открытым.'
              : 'Откройте установленную PWA по HTTPS в Chrome или Edge на совместимом устройстве. Safari/iPhone используйте для просмотра уже синхронизированных данных.'}</small>
          </div>}
          {app.transportMode !== 'wifi-hub' && <a className="sensor-emulator-link" href="/sensor-emulator/" target="_blank" rel="noreferrer">Открыть независимый эмулятор датчика</a>}
          <button className="button primary read-button" onClick={readSensor} disabled={!palletId || (app.transportMode === 'mock' && !selectedSensor) || (app.transportMode === 'wifi-hub' && !hubUrl.trim()) || busy || !transportSupported}>{phaseLabel(phase)}</button>
          {progress && <div className="read-progress"><span><i style={{ width: `${progress.total === 0 ? 100 : progress.total ? Math.min(100, progress.done / progress.total * 100) : 30}%` }} /></span><small>{progress.fileName ? `${progress.fileName} · ` : ''}{progress.done}{progress.total ? ` / ${progress.total}` : ''} ${progress.unit}</small></div>}
          {lastRead && <div className="ble-result" aria-live="polite">
            <div><span>Устройство</span><strong>{lastRead.info.serialNumber ?? lastRead.info.name}</strong></div>
            {lastRead.kind === 'sensor' ? <>
              <div><span>Грань A · канал 0</span><strong>{lastRead.sideA}</strong></div>
              <div><span>Грань B · канал 1</span><strong>{lastRead.sideB}</strong></div>
              <div><span>Всего</span><strong>{lastRead.total}</strong></div>
              <small>CRC и уникальность sequence/channel проверены до локального сохранения.</small>
            </> : <>
              <div><span>Сеанс</span><strong>{lastRead.ref.sessionId}</strong></div>
              <div><span>Проверенные файлы</span><strong>{lastRead.files.filter(file => file.verified).length} / 5</strong></div>
              <div className="hub-file-list">{lastRead.files.filter(file => file.verified).map(file => <button key={file.fileId} className="button secondary" onClick={() => downloadHubFile(lastRead.ref, file)}>{file.fileName} · {file.fileSize} байт</button>)}</div>
              <small>{lastRead.files.every(file => file.verified) && lastRead.files.length === 5
                ? 'Пять файлов проверены по CRC-32 и доступны для скачивания. Это локальная копия: синхронизация 16 каналов с сервером пока не реализована. Очистка хаба не выполняется автоматически.'
                : 'Считывание прервано. Повторите его с тем же хабом: готовые блоки сохранятся, передача продолжится с последнего подтверждённого смещения.'}</small>
            </>}
          </div>}
        </article>
        <aside className="card safety-card">
          <p className="eyebrow">Гарантии сохранности</p><h2>Данные не удаляются до подтверждения</h2>
          <ol>
            <li><span>01</span>{app.transportMode === 'wifi-hub' ? 'Wi-Fi-выгрузка принимает строго заданные диапазоны файлов и проверяет CRC-32.' : 'BLE-передача проверяется по CRC до локального сохранения.'}</li>
            <li><span>02</span>{app.transportMode === 'mock' ? 'Измерения сначала сохраняются в IndexedDB.' : 'Каждый подтверждённый блок файла сохраняется в IndexedDB; после обрыва чтение продолжается с него.'}</li>
            <li><span>03</span>{app.transportMode === 'mock' ? 'Измерения сохраняются локально и отправляются на сервер без дублей.' : 'ACK отправляется только после полной проверки пяти файлов.'}</li>
            <li><span>04</span>{app.transportMode === 'mock' ? 'Локальная запись подтверждается после ответа сервера.' : 'Исходные файлы доступны для скачивания с устройства оператора.'}</li>
          </ol>
          <div className="safety-note">{app.transportMode === 'mock' ? 'Mock-режим предназначен для приёмки интерфейса без оборудования.' : 'Файлы хаба не отправляются на сервер и не удаляются автоматически. Не очищайте хаб до архивации файлов.'}</div>
        </aside>
      </div>
    </section>
  )
}

async function downloadHubFile(ref: HubSessionRef, file: HubFileMeta) {
  try {
    const blob = await getHubFileBlob(ref, file.fileId)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${ref.serialNumber}-${ref.sessionId}-${file.fileName}`
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (error) {
    appStore.notify('error', error instanceof Error ? error.message : 'Не удалось выгрузить файл хаба')
  }
}

function phaseLabel(phase: 'idle' | 'connecting' | 'reading' | 'saving' | 'syncing') {
  return { idle: 'Начать считывание', connecting: 'Подключение…', reading: 'Считывание…', saving: 'Локальное сохранение…', syncing: 'Синхронизация…' }[phase]
}

function cornerLabel(face: Assignment['face']) {
  return { FRONT: 'передний левый угол', RIGHT: 'передний правый угол', BACK: 'задний правый угол', LEFT: 'задний левый угол' }[face]
}

function normalizeSerial(serial: string) {
  return serial.trim().toUpperCase()
}
